import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ArrowLeft, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TYPING_MS = 1400;

export default function NotFound() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [answered, setAnswered] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  // A tab opened on this page has nowhere to go back to.
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));

  useEffect(() => {
    const timer = window.setTimeout(() => setAnswered(true), TYPING_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className='flex min-h-screen items-center justify-center bg-gray-100 p-4'>
      <div className='flex w-full max-w-lg flex-col items-center'>
        <div aria-hidden className='mb-8 flex select-none items-center gap-3 text-[88px] font-semibold leading-none text-text-body'>
          <span>4</span>
          <span className='flex size-[76px] items-center justify-center rounded-full border-[10px] border-text-body bg-white motion-safe:animate-bounce'>
            {/* Inline in currentColor, so the mark follows the palette in dark mode (the .svg's fills are fixed). */}
            <svg viewBox='0 0 36 36' fill='currentColor' aria-hidden className='size-7'>
              <path d='M8.16064 20.4169C14.9117 20.4169 19.6328 23.4368 21.7512 29.7702H27.6482C25.0776 19.0974 16.284 15.3396 8.16064 15.3396V20.4169Z' />
              <path d='M8.16064 15.3395C15.6732 15.3495 20.1879 11.5519 22.2354 6.22998H27.8392C25.1249 16.821 16.5739 20.4168 8.16064 20.4168V15.3395Z' />
            </svg>
          </span>
          <span>4</span>
        </div>

        <div className='flex w-full flex-col gap-3' aria-live='polite'>
          <div className='w-fit max-w-[85%] rounded-2xl border border-gray-300 bg-white p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]'>
            <p className='text-[13px] leading-[18px] text-text-body'>
              Hi, I&rsquo;m looking for <span className='break-all font-mono text-[12px]'>{pathname}</span>
            </p>
          </div>

          <div className='flex max-w-[85%] flex-col gap-3 self-end rounded-2xl border border-purple-200 bg-purple-50 p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]'>
            {answered ? (
              <>
                <p className='text-[13px] leading-[18px] text-text-body'>
                  I looked all over Kim, and nothing lives at that address. The link may be old, or the address has a typo.
                </p>
                <p className='self-end text-[11px] text-gray-800'>Sent by Kim · resolved as not found</p>
              </>
            ) : (
              <span className='flex h-[18px] items-center gap-1' aria-label='Kim is typing'>
                {[0, 150, 300].map((delay) => (
                  <span
                    key={delay}
                    className='size-1.5 rounded-full bg-gray-700 motion-safe:animate-pulse'
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </span>
            )}
          </div>
        </div>

        <div className='mt-8 flex items-center gap-2'>
          <Button variant='outline' onClick={goBack}>
            <ArrowLeft />
            Go back
          </Button>
          <Button asChild>
            <Link to='/'>
              <Home />
              Take me home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
